// src/utils/notificationsFnct.ts — SCE Issues
// Notifications des FNC-T (lot F6, décisions de Guillaume du 23/10) : qui est
// prévenu de quoi, et le message tout composé. Pur, testable hors ligne.
//
// Comme « Envoyer au BE », l'appli compose tout (objet, mail HTML, carte Teams)
// et dépose une ligne dans SCE_Envois ; le flow 6 l'envoie selon les réglages
// « Mes notifications » de chacun. Aucun flow de plus pour les gestes.
//
// ─── Qui ───
//
// - Une FNC-T est ouverte (directement ou depuis son relevé) → son leader et son
//   expert ; chaque responsable reçoit ses actions. Un leader ou un expert
//   changé ensuite est prévenu de même.
// - Une action est confiée (ajoutée, réattribuée, responsable changé) → le
//   responsable.
// - FNC-T P&ID / PPL, EDE ou HDS : le BE clôt sa mise à jour du document → les
//   responsables des autres actions encore ouvertes (documentation, retest)
//   apprennent que leur tour est venu (utils/attenteBE).
// - Tout événement → ceux qui suivent la FNC-T (cloche), s'ils n'ont pas déjà
//   un message à eux pour ce geste.
//
// Jamais : l'auteur du geste lui-même. Qui a réglé « l'appli seulement »
// (`Appli`, ou l'ancien « Aucun ») n'a pas de message — le flow 6 lui
// enverrait le mail pour « Aucun » — mais sa ligne dans la cloche
// (`clocheDuGeste`, 03/11), comme tous les autres destinataires.
//
// Pendant l'essai des FNC-T (FNCT_EN_ESSAI) : chaque message part à l'AUTEUR
// du geste seul, objet « [Essai] », avec la liste de ceux qui l'auraient reçu.
// L'auteur n'y est pas écarté, pour qu'on puisse essayer seul.

import { LIBELLES_CATEGORIE_ACTION, type ActionFnct, type Fnct, type TypeEvenementFnct } from '@/types/fnct.types'
import { libelleCategorie } from '@/config/fnct.config'
import { estActionDuBE, estFnctAvecBE } from './attenteBE'
import { appliSeulement } from './canauxNotification'
import type { NouvelleNotification } from './cloche'
import { lienFnct } from './liensFiches'
import { echeanceEnVigueur, jourLisible, memePersonne } from './fnct'
import { CLE_BLOC_FNCT_SUIVIES, lireFnctSuivies, suitFnct } from './fnctSuivies'
import {
  carteAdaptative,
  echapper,
  enHtml,
  enteteCarte,
  enveloppeMail,
  FILET_MAIL,
  lignesFaits,
  sectionMail,
  texteCarte,
  TEXTE_MAIL,
  tronquer,
  type BlocCarte,
} from './gabaritMessage'

export interface Personne {
  nom: string
  /** Adresse ; vide = pas (encore) retrouvée dans l'annuaire → pas prévenue. */
  upn: string
}

/** Un message adressé à une personne pour ce qu'ELLE a à faire. */
export type Direct =
  | { genre: 'role'; role: 'leader' | 'expert'; quand: 'ouverture' | 'changement'; personne: Personne }
  | { genre: 'actions'; personne: Personne; actions: readonly ActionFnct[]; reprisesDe?: string; motif?: string }
  | { genre: 'suite'; personne: Personne; actions: readonly ActionFnct[]; close: ActionFnct }

export interface EvenementGeste {
  type: TypeEvenementFnct
  contenu: string
}

/** Un geste sur UNE FNC-T : ses événements (pour les abonnés) et ses messages directs. */
export interface GesteFnct {
  /** La FNC-T après le geste. */
  fnct: Fnct
  evenements: readonly EvenementGeste[]
  directs: readonly Direct[]
}

/** Ce qu'il faut savoir d'une personne : son canal et les FNC-T qu'elle suit. */
export interface ReglagesNotification {
  /** CanauxNotification brut : null = jamais réglé (les deux). */
  canaux: string | null
  suivies: readonly string[]
}

export interface ContexteNotification {
  /** Qui fait le geste (compte réel). */
  acteur: Personne
  /** `https://issues.sce-apps.sartorius.com`, sans `/` final. */
  urlApp: string
  /** Par adresse EN MINUSCULES. */
  reglages: ReadonlyMap<string, ReglagesNotification>
  essai: boolean
}

export interface MessageFnct {
  numero: string
  destinataires: string[]
  objet: string
  corpsHtml: string
  carteTeams: Record<string, unknown>
}

const cle = (upn: string): string => upn.trim().toLowerCase()

/** Une ligne de SCE_UserPrefs → ses réglages ; tolérant (JSON illisible = rien de suivi). */
export function reglagesDepuisLigne(canaux: unknown, prefs: unknown): ReglagesNotification {
  let bloc: unknown
  if (typeof prefs === 'string' && prefs.trim()) {
    try {
      const json: unknown = JSON.parse(prefs)
      if (json && typeof json === 'object') bloc = (json as Record<string, unknown>)[CLE_BLOC_FNCT_SUIVIES]
    } catch {
      bloc = undefined
    }
  }
  return { canaux: typeof canaux === 'string' && canaux.trim() ? canaux.trim() : null, suivies: lireFnctSuivies(bloc) }
}

// ─── Les messages directs d'un geste ───

/** Regroupe des actions par responsable (adresse, sinon nom). */
function parResponsable(actions: readonly ActionFnct[]): { personne: Personne; actions: ActionFnct[] }[] {
  const groupes: { personne: Personne; actions: ActionFnct[] }[] = []
  for (const a of actions) {
    if (!a.responsable.trim()) continue
    const g = groupes.find((x) =>
      a.responsableUPN && x.personne.upn ? cle(x.personne.upn) === cle(a.responsableUPN) : memePersonne(x.personne.nom, a.responsable),
    )
    if (g) g.actions.push(a)
    else groupes.push({ personne: { nom: a.responsable.trim(), upn: a.responsableUPN?.trim() ?? '' }, actions: [a] })
  }
  return groupes
}

/** Les actions confiées, une entrée par responsable ; `motif` : pourquoi elles changent de mains (30/10). */
export function directsActions(actions: readonly ActionFnct[], reprisesDe?: string, motif?: string): Direct[] {
  return parResponsable(actions).map((g) => ({
    genre: 'actions',
    personne: g.personne,
    actions: g.actions,
    ...(reprisesDe ? { reprisesDe } : {}),
    ...(motif?.trim() ? { motif: motif.trim() } : {}),
  }))
}

/** FNC-T ouverte : leader, expert (adresse à retrouver), puis les actions de chacun. */
export function directsOuverture(f: Fnct, actions: readonly ActionFnct[]): Direct[] {
  const res: Direct[] = []
  if (f.leader.trim()) res.push({ genre: 'role', role: 'leader', quand: 'ouverture', personne: { nom: f.leader.trim(), upn: f.leaderUPN?.trim() ?? '' } })
  if (f.expert.trim()) res.push({ genre: 'role', role: 'expert', quand: 'ouverture', personne: { nom: f.expert.trim(), upn: '' } })
  return [...res, ...directsActions(actions)]
}

/**
 * FNC-T P&ID / PPL, EDE ou HDS dont le BE vient de clore sa mise à jour
 * (utils/attenteBE) : les responsables des autres actions encore ouvertes
 * (documentation, retest) peuvent démarrer. Rien pour une autre FNC-T, quand
 * c'est une autre action qui se clôt, ni tant qu'une autre action du BE reste ouverte.
 */
export function directsSuite(f: Fnct, actionsDeLaFnct: readonly ActionFnct[], close: ActionFnct): Direct[] {
  if (!estFnctAvecBE(f) || !estActionDuBE(close)) return []
  if (actionsDeLaFnct.some((a) => a.id !== close.id && !a.cloture && estActionDuBE(a))) return []
  const suivantes = actionsDeLaFnct.filter((a) => a.id !== close.id && !a.cloture && !estActionDuBE(a))
  return parResponsable(suivantes).map((g) => ({ genre: 'suite', personne: g.personne, actions: g.actions, close }))
}

// ─── Le message ───

const pluriel = (n: number, un: string, plusieurs: string): string => (n > 1 ? plusieurs : un)

/** « a clos une action de » : ce que fait l'auteur, pour un abonné. */
function verbe(type: TypeEvenementFnct): string {
  switch (type) {
    case 'Création':
      return 'a créé'
    case 'Ouverture':
      return 'a ouvert'
    case 'Action ajoutée':
      return 'a ajouté une action à'
    case 'Action close':
      return 'a clos une action de'
    case 'Action reportée':
      return 'a reporté une action de'
    case 'Action modifiée':
      return 'a modifié une action de'
    case 'Action réattribuée':
      return 'a réattribué des actions de'
    case 'Risque évalué':
      return 'a évalué le risque de'
    case 'Modification':
      return 'a modifié'
    case 'Clôture':
      return 'a clos'
    case 'Réouverture':
      return 'a rouvert'
    case 'Rapport':
      return 'a déposé le rapport client de'
    case 'Écart relevé':
      return 'a relevé un écart sur'
    case 'Écart modifié':
      return 'a modifié un écart de'
    case 'Écart retiré':
      return 'a retiré un écart de'
    default: {
      const jamais: never = type
      return String(jamais)
    }
  }
}

/** « Action close », « Action close (3) », « Action close, Clôture ». */
function resumeEvenements(evenements: readonly EvenementGeste[]): string {
  const types = [...new Set(evenements.map((e) => e.type))]
  if (types.length === 1 && evenements.length > 1) return `${types[0]} (${evenements.length})`
  return types.join(', ')
}

function faitsFnct(f: Fnct): Array<[string, string]> {
  const faits: Array<[string, string]> = [
    ['Équipement', [f.numerosSerie.join(', '), f.modele].filter((v) => v.trim()).join(' · ')],
    ['Projet', f.cve],
    ['Catégorie', f.categorie ? libelleCategorie(f.categorie) : ''],
    ['Leader', f.leader],
    ['État', f.cloture ? `Close le ${jourLisible(f.cloture)}` : f.ouverture ? '' : 'En relevé'],
  ]
  return faits.filter(([, v]) => v.trim() !== '')
}

/** « Confinement et correction · pour le 12/11/2026 » */
function detailAction(a: ActionFnct): string {
  const echeance = echeanceEnVigueur(a)
  return [a.categorie ? LIBELLES_CATEGORIE_ACTION[a.categorie].fr : '', echeance ? `pour le ${jourLisible(echeance)}` : ''].filter(Boolean).join(' · ')
}

/** Ce que dit un message : en clair, puis mis en forme pour le mail et la carte. */
interface Contenu {
  /** Phrases d'introduction (texte brut ; le nom de l'auteur en tête est mis en gras). */
  intro: string[]
  /** Dans l'objet, après le N°. */
  quoi: string
  sections: { titre: string; lignes: { gras: string; texte: string }[] }[]
  /** Lignes du pied du mail (texte brut). */
  pied: string[]
  /** La ligne de la cloche (03/11) : « **Auteur** quoi **FNC-T_2026-58** », l'extrait, la raison. */
  cloche: { quoi: string; extrait: string; raison: string }
}

function sectionActions(titre: string, actions: readonly ActionFnct[]): Contenu['sections'][number] {
  return { titre, lignes: actions.map((a) => ({ gras: a.description, texte: detailAction(a) })) }
}

function contenuDirect(directs: readonly Direct[], acteur: string): Contenu {
  const roles = directs.flatMap((d) => (d.genre === 'role' ? [d] : []))
  const confiees = directs.flatMap((d) => (d.genre === 'actions' ? [d] : []))
  const suites = directs.flatMap((d) => (d.genre === 'suite' ? [d] : []))
  const intro: string[] = []
  const sections: Contenu['sections'] = []
  let quoi = ''

  if (roles.length > 0) {
    const ouverture = roles.some((r) => r.quand === 'ouverture')
    const role = [...new Set(roles.map((r) => r.role))].join(' et ')
    intro.push(`${acteur} ${ouverture ? 'a ouvert' : 'a modifié'} cette FNC-T. Votre rôle : ${role}.`)
    quoi = ouverture ? `ouverte · votre rôle : ${role}` : `votre rôle : ${role}`
  }
  for (const s of suites) {
    intro.push(`${acteur} a clos l’action « ${s.close.description} » : ${pluriel(s.actions.length, 'votre action peut démarrer', 'vos actions peuvent démarrer')}.`)
    sections.push(sectionActions(pluriel(s.actions.length, 'Votre action', 'Vos actions'), s.actions))
    quoi ||= pluriel(s.actions.length, 'votre action peut démarrer', 'vos actions peuvent démarrer')
  }
  const actions = confiees.flatMap((d) => d.actions)
  if (actions.length > 0) {
    const n = actions.length
    const reprises = confiees.find((d) => d.reprisesDe)?.reprisesDe
    const motif = confiees.find((d) => d.motif)?.motif
    const combien = pluriel(n, 'une action', `${n} actions`)
    if (reprises) intro.push(`${acteur} vous confie ${combien} jusqu’ici au nom de ${reprises}.${motif ? ` Pourquoi : « ${motif} »` : ''}`)
    else if (intro.length > 0) intro.push(pluriel(n, 'Une action vous est confiée.', `${n} actions vous sont confiées.`))
    else intro.push(`${acteur} vous confie ${combien}.`)
    sections.push(sectionActions(pluriel(n, 'Votre action', 'Vos actions'), actions))
    quoi ||= pluriel(n, 'une action pour vous', `${n} actions pour vous`)
  }
  // La cloche dit d'abord ce qu'on a à faire : une action confiée, puis une
  // action qui peut démarrer, puis un rôle.
  const role = [...new Set(roles.map((r) => r.role))].join(' et ')
  const enAttente = suites.flatMap((s) => s.actions)
  const quoiCloche =
    actions.length > 0
      ? `vous confie ${pluriel(actions.length, 'une action', `${actions.length} actions`)} de`
      : enAttente.length > 0
        ? `a clos l’action du BE : ${pluriel(enAttente.length, 'votre action peut démarrer', 'vos actions peuvent démarrer')} sur`
        : `vous nomme ${role} de`
  const extrait = [...actions, ...enAttente].map((a) => [a.description, detailAction(a)].filter(Boolean).join(' · ')).join('\n')
  return {
    intro,
    quoi,
    sections,
    pied: [`Répondre à ce mail écrit directement à ${acteur}.`],
    cloche: { quoi: quoiCloche, extrait, raison: '' },
  }
}

function contenuAbonne(evenements: readonly EvenementGeste[], acteur: string): Contenu {
  const seul = evenements.length === 1 ? evenements[0] : undefined
  return {
    intro: [`${acteur} ${seul ? verbe(seul.type) : 'a mis à jour'} cette FNC-T, que vous suivez.`],
    quoi: resumeEvenements(evenements).toLowerCase(),
    sections: [
      {
        titre: 'Ce qui a changé',
        lignes: evenements.map((e) => ({ gras: e.type, texte: tronquer(e.contenu, 600) })),
      },
    ],
    pied: ['Vous suivez cette FNC-T : la cloche de sa fiche arrête ces messages.', `Répondre à ce mail écrit directement à ${acteur}.`],
    cloche: {
      quoi: seul ? verbe(seul.type) : 'a mis à jour',
      extrait: evenements[evenements.length - 1]?.contenu ?? '',
      raison: 'Vous suivez cette FNC-T',
    },
  }
}

function mettreEnForme(f: Fnct, c: Contenu, ctx: ContexteNotification, essaiPour: readonly string[] | null): Omit<MessageFnct, 'destinataires'> {
  const lien = `${ctx.urlApp}/fnct/${encodeURIComponent(f.numero)}`
  const titre = tronquer(f.description || f.numero, 200)
  const acteur = ctx.acteur.nom
  const avertissement = essaiPour
    ? `Essai des notifications FNC-T : en production, ce message irait à ${essaiPour.join(', ')}.`
    : ''

  // L'auteur en gras quand la phrase commence par lui.
  const phrase = (t: string): string =>
    acteur && t.startsWith(`${acteur} `) ? `<b style="color:#000000">${echapper(acteur)}</b>${enHtml(t.slice(acteur.length))}` : enHtml(t)
  const bandeauEssai = avertissement
    ? `<div style="margin-bottom:10px;border-left:3px solid #FFED00;background-color:#F5F5F5;padding:8px 12px;font-size:13px;color:#000000">${echapper(avertissement)}</div>`
    : ''
  const sections = c.sections
    .map((s) =>
      sectionMail(
        echapper(s.titre),
        s.lignes
          .map(
            (l, i) =>
              `<div style="padding:6px 0;${i > 0 ? `border-top:1px solid ${FILET_MAIL};` : ''}font-size:14px;line-height:20px;color:${TEXTE_MAIL}">` +
              `<b style="color:#000000">${enHtml(l.gras)}</b>${l.texte ? `<br>${enHtml(l.texte)}` : ''}</div>`,
          )
          .join(''),
      ),
    )
    .join('')

  const corpsHtml = enveloppeMail({
    intro: bandeauEssai + c.intro.map(phrase).join('<br>'),
    surtitre: f.numero,
    titre,
    faits: lignesFaits(faitsFnct(f)),
    sections,
    lien,
    libelleBouton: 'Ouvrir la FNC-T dans SCE Issues',
    pied: c.pied.map(echapper).join('<br>'),
  })

  const corps: BlocCarte[] = [enteteCarte()]
  if (avertissement) corps.push({ type: 'Container', style: 'emphasis', spacing: 'Medium', items: [texteCarte(avertissement, { size: 'Small' })] })
  corps.push(
    texteCarte(c.intro.join('\n\n'), { isSubtle: true, size: 'Small', spacing: 'Medium' }),
    texteCarte(`${f.numero} · ${titre}`, { weight: 'Bolder', size: 'Medium', spacing: 'Small' }),
    { type: 'FactSet', facts: faitsFnct(f).map(([title, value]) => ({ title, value })) },
  )
  for (const s of c.sections) {
    corps.push(texteCarte(s.titre, { weight: 'Bolder', spacing: 'Medium' }))
    for (const l of s.lignes) {
      corps.push({
        type: 'Container',
        spacing: 'Small',
        items: [texteCarte(l.gras, { weight: 'Bolder', size: 'Small' }), ...(l.texte ? [texteCarte(l.texte, { spacing: 'None' })] : [])],
      })
    }
  }

  const objet = tronquer(`${essaiPour ? '[Essai] ' : ''}[SCE Issues] ${f.numero} · ${c.quoi} · ${tronquer(f.description, 80)}`, 250)
  return { numero: f.numero, objet, corpsHtml, carteTeams: carteAdaptative(corps, lien) }
}

/**
 * Les messages d'un geste : un par personne qui a quelque chose à faire, puis
 * un pour tous les abonnés qui n'ont rien reçu d'autre. Les personnes sans
 * adresse, l'auteur et « Aucun » sont écartés (en essai, l'auteur reçoit tout).
 */
export function messagesDuGeste(g: GesteFnct, ctx: ContexteNotification): MessageFnct[] {
  const messages: MessageFnct[] = []
  for (const { pour, contenu } of envoisDuGeste(g, ctx, false)) {
    if (ctx.essai) {
      if (!ctx.acteur.upn.includes('@')) continue
      messages.push({ ...mettreEnForme(g.fnct, contenu, ctx, pour.map((p) => p.libelle)), destinataires: [ctx.acteur.upn] })
    } else {
      messages.push({ ...mettreEnForme(g.fnct, contenu, ctx, null), destinataires: pour.map((p) => p.upn) })
    }
  }
  return messages
}

/**
 * La cloche d'un geste (03/11) : les mêmes personnes que les messages, plus
 * celles réglées sur « l'appli seulement » (la cloche est leur seul canal). En
 * essai, tout revient à l'auteur, comme les messages.
 */
export function clocheDuGeste(g: GesteFnct, ctx: ContexteNotification): NouvelleNotification[] {
  const lignes: NouvelleNotification[] = []
  for (const { pour, contenu } of envoisDuGeste(g, ctx, true)) {
    const destinataires = ctx.essai ? (ctx.acteur.upn.includes('@') ? [ctx.acteur.upn] : []) : pour.map((p) => p.upn)
    for (const destinataire of destinataires) {
      lignes.push({
        destinataire,
        reference: g.fnct.numero,
        genre: 'fnct',
        auteur: ctx.acteur.nom,
        auteurUpn: ctx.acteur.upn,
        quoi: contenu.cloche.quoi,
        libelle: g.fnct.description || g.fnct.numero,
        extrait: contenu.cloche.extrait,
        raison: contenu.cloche.raison,
        lien: lienFnct(g.fnct.numero),
      })
    }
  }
  return lignes
}

/** Qui reçoit quoi pour ce geste : un contenu par personne qui a à faire, puis un pour les abonnés. */
function envoisDuGeste(
  g: GesteFnct,
  ctx: ContexteNotification,
  avecAppliSeule: boolean,
): { pour: { upn: string; libelle: string }[]; contenu: Contenu }[] {
  const joignable = (upn: string): boolean => {
    if (!upn.includes('@')) return false
    if (!ctx.essai && cle(upn) === cle(ctx.acteur.upn)) return false
    return avecAppliSeule || !appliSeulement(ctx.reglages.get(cle(upn))?.canaux)
  }

  // Les directs, regroupés par personne (leader ET responsable = un message).
  const parPersonne = new Map<string, { personne: Personne; directs: Direct[] }>()
  for (const d of g.directs) {
    if (!joignable(d.personne.upn)) continue
    const k = cle(d.personne.upn)
    const deja = parPersonne.get(k)
    if (deja) deja.directs.push(d)
    else parPersonne.set(k, { personne: d.personne, directs: [d] })
  }
  const envois: { pour: { upn: string; libelle: string }[]; contenu: Contenu }[] = []
  for (const { personne, directs } of parPersonne.values()) {
    envois.push({ pour: [{ upn: personne.upn.trim(), libelle: personne.nom || personne.upn }], contenu: contenuDirect(directs, ctx.acteur.nom) })
  }

  // Les abonnés, ensemble, s'il y a de quoi leur dire.
  if (g.evenements.length > 0) {
    const abonnes = [...ctx.reglages.entries()]
      .filter(([upn, r]) => suitFnct(r.suivies, g.fnct.numero) && joignable(upn) && !parPersonne.has(upn))
      .map(([upn]) => ({ upn, libelle: upn }))
    if (abonnes.length > 0) envois.push({ pour: abonnes, contenu: contenuAbonne(g.evenements, ctx.acteur.nom) })
  }
  return envois
}

/**
 * La ligne de SCE_Envois (mêmes colonnes que l'envoi au BE, flow 6) : jamais de
 * copie à l'auteur, « Répondre à » lui écrit.
 */
export function champsNotification(m: MessageFnct, expediteur: string): Record<string, string> {
  return {
    Title: m.numero,
    Destinataires: m.destinataires.join(';'),
    Objet: m.objet,
    CorpsHtml: m.corpsHtml,
    CarteTeams: JSON.stringify(m.carteTeams),
    Note: '',
    Expediteur: expediteur,
    CopieExpediteur: 'Non',
  }
}
